import React, { useRef, useState } from 'react';
import { Alert, Button, CircularProgress, DialogActions, DialogContent, DialogTitle, Typography } from '@mui/material';
import { sbg } from '../../theme/sbgTokens';

interface ConfirmationModalProps {
  onClose: () => void;
  onConfirm?: () => void | Promise<unknown>;
  onPendingChange?: (pending: boolean) => void;
  title: string;
  message: string;
  /** Verb + object that repeats the title's verb ("Approve request", "Delete employee"). */
  confirmText: string;
  /** Shown on the confirm button while `onConfirm` runs ("Approving…"). */
  pendingText?: string;
  cancelText?: string;
  severity?: 'warning' | 'error' | 'info' | 'success';
}

/** The title's leading verb ("Approve Maria Chen’s PTO?" → "Approve"), for callers that omit `confirmText`. */
const verbFromTitle = (title: string) => {
  const word = title.trim().split(/\s+/)[0]?.replace(/[^A-Za-z-]/g, '');
  return word || 'Continue';
};

/** The backend's own explanation (`{ error: '…' }` on an RTK Query rejection), ending in a period. */
const failureReason = (err: unknown): string | null => {
  const data = (err as { data?: { error?: unknown } } | null)?.data;
  const raw = typeof data?.error === 'string' ? data.error.trim() : '';
  if (!raw) return null;
  return /[.!?]$/.test(raw) ? raw : `${raw}.`;
};

/**
 * The shared confirmation rendered by ModalManager (it supplies the Dialog,
 * `aria-labelledby="confirmation-title"` and `aria-describedby="confirmation-message"`).
 * Styled as the kit's ConfirmDialog (book §11.14): a heading-3 title that names
 * the object, the consequence in secondary body text, a ghost Cancel, and a
 * confirm button that repeats the verb. `severity="error"` makes it a Danger
 * confirm; every other severity uses Primary Blue.
 */
const ConfirmationModal: React.FC<ConfirmationModalProps> = ({
  onClose,
  onConfirm,
  onPendingChange,
  title,
  message,
  confirmText,
  pendingText = 'Working…',
  cancelText = 'Cancel',
  severity = 'warning',
}) => {
  const [pending, setPending] = useState(false);
  const [failed, setFailed] = useState(false);
  const [failedReason, setFailedReason] = useState<string | null>(null);
  const pendingRef = useRef(false);
  const confirmLabel = confirmText || verbFromTitle(title);

  const handleConfirm = async () => {
    if (!onConfirm || pendingRef.current) return;
    pendingRef.current = true;
    setPending(true);
    setFailed(false);
    setFailedReason(null);
    onPendingChange?.(true);
    try {
      await onConfirm();
      onClose();
    } catch (err) {
      setFailedReason(failureReason(err));
      setFailed(true);
    } finally {
      pendingRef.current = false;
      setPending(false);
      onPendingChange?.(false);
    }
  };

  const destructive = severity === 'error';

  return (
    <>
      <DialogTitle id="confirmation-title">{title}</DialogTitle>
      <DialogContent>
        <Typography id="confirmation-message" variant="body1" sx={{ color: sbg.text.secondary, whiteSpace: 'pre-line' }}>
          {message}
        </Typography>
        {failed && (
          <Alert severity="error" sx={{ mt: 2 }}>
            {failedReason
              ? `This didn’t go through. ${failedReason}`
              : 'The action could not be completed. Please try again.'}
          </Alert>
        )}
      </DialogContent>
      <DialogActions>
        <Button
          autoFocus
          disabled={pending}
          onClick={() => { if (!pendingRef.current) onClose(); }}
          variant={onConfirm ? 'text' : 'outlined'}
        >
          {onConfirm ? cancelText : 'Close'}
        </Button>
        {onConfirm && (
          <Button
            onClick={handleConfirm}
            disabled={pending}
            variant="contained"
            color={destructive ? 'error' : 'primary'}
            aria-busy={pending || undefined}
            startIcon={pending ? <CircularProgress size={16} thickness={5} color="inherit" aria-hidden="true" /> : undefined}
          >
            {pending ? pendingText : confirmLabel}
          </Button>
        )}
      </DialogActions>
    </>
  );
};

export default ConfirmationModal;
